import { useEffect, useState } from 'react';
import nacmLogo from '../../../../assets/svg/nacm.svg';

interface ScoreClass {
  grade: string;
  label: string;
  description: string;
  min: number;
  max: number;
  color: string;
  bg: string;
  text: string;
  dot: string;
}

interface GaugeSVGProps {
  score: number;
  scoreClass: ScoreClass;
}

interface NACMScoreCardProps {
  score?: number | string;
  creditLimit?: string;
}

const SCORE_CLASSES: ScoreClass[] = [
  {
    grade: '1',
    label: 'Very Low Risk',
    description: 'Score Range 760 - 900',
    min: 760,
    max: 900,
    color: '#1D9E75',
    bg: '#EAF3DE',
    text: '#3B6D11',
    dot: '#1D9E75',
  },
  {
    grade: '2',
    label: 'Low Risk',
    description: 'Score Range 700 - 759',
    min: 700,
    max: 759,
    color: '#5DCAA5',
    bg: '#E1F5EE',
    text: '#0F6E56',
    dot: '#5DCAA5',
  },
  {
    grade: '3',
    label: 'Low to Moderate Risk',
    description: 'Score Range 630 - 699',
    min: 630,
    max: 699,
    color: '#EF9F27',
    bg: '#FAEEDA',
    text: '#854F0B',
    dot: '#EF9F27',
  },
  {
    grade: '4A',
    label: 'High Risk',
    description: 'Score Range 560 - 629',
    min: 560,
    max: 629,
    color: '#FA8C27',
    bg: '#FFF0E0',
    text: '#A05010',
    dot: '#FA8C27',
  },
  {
    grade: '4B',
    label: 'Very High Risk',
    description: 'Score Range 500 - 559',
    min: 500,
    max: 559,
    color: '#FAC775',
    bg: '#FFF8EC',
    text: '#BA7517',
    dot: '#FAC775',
  },
  {
    grade: '5',
    label: 'Extreme Risk',
    description: 'Score Range 450 - 499',
    min: 450,
    max: 499,
    color: '#E24B4A',
    bg: '#FCEBEB',
    text: '#A32D2D',
    dot: '#E24B4A',
  },
  {
    grade: 'NA',
    label: 'Score Not Available',
    description: 'See detailed description below',
    min: 0,
    max: 449,
    color: '#888780',
    bg: '#F1EFE8',
    text: '#444441',
    dot: '#888780',
  },
];

function getScoreClass(score: number): ScoreClass {
  return SCORE_CLASSES.find((s) => score >= s.min && score <= s.max) ?? SCORE_CLASSES[6];
}

// Maps score 450–900 to 0–100% for gauge
function scoreToPercent(score: number): number {
  const clamped = Math.min(900, Math.max(450, score));
  return ((clamped - 450) / (900 - 450)) * 100;
}

function GaugeSVG({ score, scoreClass }: GaugeSVGProps) {
  const [animated, setAnimated] = useState<number>(0);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(scoreToPercent(score)), 100);
    return () => clearTimeout(t);
  }, [score]);

  const R = 80;
  const cx = 110;
  const cy = 110;
  const startAngle = Math.PI;
  const totalArc = Math.PI;

  const arcPath = (start: number, end: number, r: number): string => {
    const s = { x: cx + r * Math.cos(start), y: cy + r * Math.sin(start) };
    const e = { x: cx + r * Math.cos(end), y: cy + r * Math.sin(end) };
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const angle = startAngle + totalArc * (animated / 100);
  const knobX = cx + R * Math.cos(angle);
  const knobY = cy + R * Math.sin(angle);

  return (
    <svg viewBox="0 0 220 130" width="220" height="130">
      {/* Track */}
      <path
        d={arcPath(Math.PI, 2 * Math.PI, R)}
        fill="none"
        stroke="#E8E6DF"
        strokeWidth="14"
        strokeLinecap="round"
      />
      {/* Colored progress arc */}
      <path
        d={arcPath(Math.PI, Math.PI + totalArc * (animated / 100), R)}
        fill="none"
        stroke={scoreClass.color}
        strokeWidth="14"
        strokeLinecap="round"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      {/* Knob */}
      <circle
        cx={knobX}
        cy={knobY}
        r="9"
        fill="white"
        stroke={scoreClass.color}
        strokeWidth="3"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      {/* Score number */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontSize="34"
        fontWeight="600"
        fill={scoreClass.color}>
        {score}
      </text>
      {/* Sub-label */}
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#888780">
        range: 450 – 900
      </text>
    </svg>
  );
}

export default function NACMScoreCard({ score = 610 }: NACMScoreCardProps) {
  const currentScore = isNaN(Number(score)) ? 0 : Number(score);
  const scoreClass = getScoreClass(currentScore);

  const tableHeaders = ['Class', 'Risk Level', 'Score Range'];

  return (
    <div className="flex flex-col items-center justify-center bg-white rounded-lg border border-[#E8E6DF] w-full p-2 mb-4">
      {/* Score Panel */}
      <div className="w-full grid grid-cols-[1fr_1.4fr_1fr] items-center p-2.5 border-b border-[#EEECE6]">
        {/* Risk badge */}
        <div className="flex flex-col gap-1.5">
          <div className="w-full mb-2">
            <img src={nacmLogo} alt="Credit Safe" className="w-auto h-12 object-contain" />
          </div>
          <span className="text-[11px] text-[#888780] uppercase tracking-[0.08em]">Risk Level</span>
          <span
            className="inline-flex items-center gap-[7px] px-4 py-[7px] rounded-[30px] text-[13px] font-semibold w-fit border"
            style={{
              background: scoreClass.bg,
              color: scoreClass.text,
              borderColor: `${scoreClass.color}44`,
            }}>
            <span
              className="inline-block w-[9px] h-[9px] rounded-full shrink-0"
              style={{ background: scoreClass.color }}
            />
            {scoreClass.label}
          </span>
        </div>

        {/* Gauge */}
        <div className="flex flex-col items-center">
          <span className="text-base text-[#888780] mb-0.5">Credit Score</span>
          <GaugeSVG score={currentScore} scoreClass={scoreClass} />
        </div>

        {/* Credit limit */}
        <div className="text-right flex flex-col">
          <span className="text-xs text-[#888780] mt-1">
            Class: <strong style={{ color: scoreClass.color }}>{scoreClass.grade}</strong>
          </span>
          <span className="text-[11px] text-[#888780]">Risk doubles every 60‑point drop</span>
        </div>
      </div>

      {/* Risk Table */}
      <div className="p-2.5 w-full">
        <p className="text-xs text-[#888780] uppercase tracking-[0.08em] mb-3.5">
          Score Class Reference
        </p>
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {tableHeaders.map((h) => (
                <th
                  key={h}
                  className="text-left text-[#888780] font-medium px-3 py-1.5 border-b border-[#EEECE6] text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SCORE_CLASSES.map((s) => {
              const isActive = s.grade === scoreClass.grade;
              return (
                <tr
                  key={s.grade}
                  className="transition-colors duration-200"
                  style={{ background: isActive ? s.bg : 'transparent' }}>
                  {/* Grade + dot */}
                  <td className="px-3 py-[9px] border-b border-[#EEECE6]">
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: s.dot }}
                      />
                      <strong
                        className={isActive ? 'font-bold' : 'font-medium'}
                        style={{ color: isActive ? s.color : '#1A1A18' }}>
                        {s.grade}
                      </strong>
                    </span>
                  </td>

                  {/* Risk label */}
                  <td className="px-3 py-[9px] border-b border-[#EEECE6]">
                    <span
                      className="inline-block px-[11px] py-[3px] rounded-[20px] text-xs font-medium"
                      style={{ background: s.bg, color: s.text }}>
                      {s.label}
                      {isActive && <span className="ml-[5px] text-[10px]">◀ current</span>}
                    </span>
                  </td>

                  {/* Score range */}
                  <td
                    className={`px-3 py-[9px] border-b border-[#EEECE6] tabular-nums text-xs ${
                      isActive ? 'font-bold' : 'font-normal'
                    }`}
                    style={{ color: isActive ? s.color : '#444441' }}>
                    {s.grade === 'NA' ? 'N/A' : `${s.min} – ${s.max}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
